import React from 'react'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import managementService from '../../services/managementService'
import ManagementReportShell from './ManagementReportShell'
import useManagementReport from './useManagementReport'
import { DataTable, DrillLinks, HealthBadge, KpiGrid, KpiTile, Section, Unavailable } from './ManagementWidgets'
import { at, exportToSpreadsheet, flattenMetrics, formatINR, isMasked, isMetric, metricNumber } from './managementUtils'

const LINKS = [
  { label: 'Budget vs actual', to: '/expenses/budget-vs-actual' },
  { label: 'Burn reports', to: '/expenses/burn-reports' },
  { label: 'Burn entries', to: '/expenses/burn-entry' },
  { label: 'Burn discipline', to: '/accounts/reports/burn-discipline' }
]

const STATUS_TONE = { OVER: 'Red', UNDER: 'Green', NO_BUDGET: 'Grey' }
const STATUS_LABEL = { OVER: 'Over budget', UNDER: 'Within budget', NO_BUDGET: 'No budget' }

export const CATEGORY_COLUMNS = [
  { key: 'categoryName', label: 'Category' },
  { key: 'group', label: 'Group' },
  { key: 'budgetToDate', label: 'Budget to date', format: 'currency' },
  { key: 'actualToDate', label: 'Actual to date', format: 'currency' },
  { key: 'variance', label: 'Variance', format: 'currency' },
  { key: 'variancePercent', label: 'Variance %', format: 'signedPercent' },
  { key: 'status', label: 'Status', render: (v) => <HealthBadge status={STATUS_TONE[v]} label={STATUS_LABEL[v] || v} /> }
]

const monthLabel = (key) => {
  const [y, m] = String(key).split('-').map(Number)
  return y && m ? new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }) : key
}

export const BudgetTrendChart = ({ trend }) => {
  const rows = (trend || []).map((t) => ({ month: monthLabel(t.month), budget: metricNumber(t.budget), actual: metricNumber(t.actual) }))
  if (!rows.length) return <Unavailable>No monthly burn recorded this financial year</Unavailable>
  return (
    <div style={{ width: '100%', height: 240 }}>
      <ResponsiveContainer>
        <BarChart data={rows} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="month" fontSize={11} />
          <YAxis fontSize={11} tickFormatter={(v) => formatINR(v, { compact: true })} width={80} />
          <Tooltip formatter={(v) => (v === null ? 'Data unavailable' : formatINR(v))} />
          <Legend />
          <Bar dataKey="budget" name="Budget" fill="#B0BEC5" radius={[3, 3, 0, 0]} />
          <Bar dataKey="actual" name="Actual" fill="#222448" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

const ExpenseBudgetPerformanceReport = () => {
  const report = useManagementReport(managementService.getExpenseBudget)

  const onExport = (data) => exportToSpreadsheet('expense-budget', {
    Summary: flattenMetrics({ budget: data.budget, burn: data.burn, projectExpenses: data.projectExpenses }),
    Categories: Array.isArray(data.burn?.byCategory) ? data.burn.byCategory : [],
    'Monthly trend': Array.isArray(data.burn?.monthlyTrend) ? data.burn.monthlyTrend : []
  })

  return (
    <ManagementReportShell
      title="Expense & Budget Performance"
      subtitle="Approved burn against the approved or frozen financial-year budget"
      report={report}
      onExport={onExport}
    >
      {(d) => {
        const budget = d.budget
        const burnMasked = isMasked(d.burn)
        return (
          <>
            <DrillLinks links={LINKS} />
            <Section title="Budget">
              {isMetric(budget)
                ? <KpiGrid><KpiTile label="Approved budget" metric={budget} format="currency" to="/expenses/budget-vs-actual" /></KpiGrid>
                : (
                  <KpiGrid>
                    <KpiTile label="Financial year" metric={{ value: budget?.financialYear, status: 'actual' }} />
                    <KpiTile label="Status" metric={{ value: `${budget?.status || '—'} (rev ${budget?.revision ?? 0})`, status: 'actual' }} />
                    <KpiTile label="Annual budget" metric={budget?.totalAnnualBudget} format="currency" />
                    <KpiTile label="Budget to date" metric={budget?.budgetToDate} format="currency" />
                  </KpiGrid>
                )}
            </Section>

            <Section title="Burn vs budget">
              <KpiGrid>
                <KpiTile label="Period actual" metric={at(d, 'burn.periodActual')} format="currency" />
                <KpiTile label="FY to date actual" metric={at(d, 'burn.fyToDateActual')} format="currency" />
                <KpiTile label="Variance to date" metric={at(d, 'burn.varianceToDate')} format="currency" />
                <KpiTile label="Variance %" metric={at(d, 'burn.variancePercentToDate')} format="signedPercent" />
                <KpiTile label="Budget utilisation" metric={at(d, 'burn.budgetUtilisationPercent')} format="percent" />
                <KpiTile label="Month-on-month" metric={at(d, 'burn.monthOnMonthGrowthPercent')} format="signedPercent" />
                <KpiTile label="Categories over budget" metric={at(d, 'burn.overBudgetCategories')} tone="red" />
                <KpiTile label="Project expenses (period)" metric={at(d, 'projectExpenses.approvedInPeriod')} format="currency" />
              </KpiGrid>
            </Section>

            <Section title="Monthly budget vs actual">
              {burnMasked ? <Unavailable note="Restricted — requires sensitive finance permission" /> : <BudgetTrendChart trend={d.burn?.monthlyTrend} />}
            </Section>

            <Section title="By category" subtitle="Financial year to date">
              {burnMasked
                ? <Unavailable note="Restricted — requires sensitive finance permission" />
                : <DataTable rows={d.burn?.byCategory || []} columns={CATEGORY_COLUMNS} emptyText="No approved burn or budget lines recorded." />}
            </Section>
          </>
        )
      }}
    </ManagementReportShell>
  )
}

export default ExpenseBudgetPerformanceReport
